/**
 * マップスタイル（新世代 GeoMaps）の選択状態とスタイル URL の組み立て（要件19 / タスク20.1）。
 *
 * 旧世代 ALS（`maps/v0` ＋ `VectorOpenDataStandardLight`＝OSM Open Data）は建物名・POI が
 * 乏しかったため、新世代 GeoMaps（HERE 由来の商用データ）へ移行した。GeoMaps はリソースレスで、
 * スタイル名を URL に埋めるだけで切り替わる（Map リソースをスタイルごとに作る必要はない）。
 *
 * 採用する3スタイル:
 *   - Standard   … POI・建物名が豊富。既定
 *   - Monochrome … 淡色。参加者ピン・軌跡が目立つ
 *   - Hybrid     … 衛星画像＋道路/POI ラベル
 * `Satellite` は symbol レイヤーを持たず（ラベルが一切出ない）「地図情報が乏しい」という
 * 元の不満と逆行するため採用しない。Hybrid を「衛星」として提供する。
 *
 * 参照: https://docs.aws.amazon.com/location/latest/APIReference/API_geomaps_GetStyleDescriptor.html
 */
import { useCallback, useState } from 'react';
import { addProtocol } from 'maplibre-gl';
import { patchGeoMapsStyle } from './stylePatch';

/** GeoMaps のスタイル名（スタイル URL のパスにそのまま入る）。 */
export type MapStyleId = 'Standard' | 'Monochrome' | 'Hybrid';

/** UI に並べる順序。MapToolbar のスタイル選択肢の順と一致させる。 */
export const MAP_STYLE_IDS: readonly MapStyleId[] = [
  'Standard',
  'Monochrome',
  'Hybrid',
];

/** 既定スタイル（POI が最も豊富なもの）。 */
export const DEFAULT_MAP_STYLE: MapStyleId = 'Standard';

const STORAGE_KEY = 'reinvent_map_style';

/**
 * スタイル記述子を取得するためのカスタムプロトコル名。
 *
 * MapLibre に `https://...` を直接渡すと公式スタイル定義をそのまま読み込み、style-spec 違反の
 * 警告が DevTools に大量に出る（`stylePatch.ts` 参照）。カスタムプロトコルで割り込み、
 * 取得した JSON を補正してから MapLibre に渡す。`addProtocol` は取得関数を差し替えるだけで
 * リクエスト回数は増えない（MapLibre が取りに行く代わりに自前で1回 fetch する）。
 *
 * 記述子内のタイル・glyphs・sprite の URL は絶対 https なので、このプロトコルを通るのは
 * スタイル記述子だけ。
 *
 * 参照: https://maplibre.org/maplibre-gl-js/docs/API/functions/addProtocol/
 */
const STYLE_PROTOCOL = 'geomaps';

let protocolRegistered = false;

/** スタイル記述子用プロトコルを一度だけ登録する。 */
function ensureStyleProtocol(): void {
  if (protocolRegistered) {
    return;
  }
  protocolRegistered = true;
  addProtocol(STYLE_PROTOCOL, async (params, abortController) => {
    const url = params.url.replace(`${STYLE_PROTOCOL}://`, 'https://');
    const res = await fetch(url, { signal: abortController.signal });
    if (!res.ok) {
      throw new Error(`GeoMaps style descriptor failed: ${res.status}`);
    }
    return { data: patchGeoMapsStyle(await res.json()) };
  });
}

/** GeoMaps のスタイル記述子 URL を組み立てる（取得は上記プロトコル経由）。 */
export function buildStyleUrl(style: MapStyleId): string {
  ensureStyleProtocol();
  const region = import.meta.env.VITE_ALS_REGION;
  const apiKey = import.meta.env.VITE_ALS_API_KEY;
  return `${STYLE_PROTOCOL}://maps.geo.${region}.amazonaws.com/v2/styles/${style}/descriptor?key=${apiKey}&color-scheme=Light`;
}

/** localStorage からスタイルを読む（未設定・不正値は既定）。 */
function loadMapStyle(): MapStyleId {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return MAP_STYLE_IDS.includes(v as MapStyleId)
      ? (v as MapStyleId)
      : DEFAULT_MAP_STYLE;
  } catch {
    return DEFAULT_MAP_STYLE;
  }
}

export interface UseMapStyleResult {
  mapStyle: MapStyleId;
  setMapStyle: (style: MapStyleId) => void;
}

/**
 * スタイル選択フック（i18n/lang.ts の useLang と同じ形）。
 * App で1回呼び、MapView（右上ツールバー）に配布する。
 */
export function useMapStyle(): UseMapStyleResult {
  const [mapStyle, setMapStyleState] = useState<MapStyleId>(() =>
    loadMapStyle(),
  );

  const setMapStyle = useCallback((next: MapStyleId) => {
    setMapStyleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // 永続化失敗（プライベートモード等）は無視。セッション内の切替は効く。
    }
  }, []);

  return { mapStyle, setMapStyle };
}
